import { openDB } from 'idb';

const DB_NAME = 'pomogrow';
const DB_VERSION = 1;

let dbPromise;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Sessions store
        if (!db.objectStoreNames.contains('sessions')) {
          const sessionStore = db.createObjectStore('sessions', { keyPath: 'id' });
          sessionStore.createIndex('by-date', 'date');
          sessionStore.createIndex('by-status', 'status');
        }

        // User state (streaks, XP, settings, onboarding)
        if (!db.objectStoreNames.contains('userState')) {
          db.createObjectStore('userState', { keyPath: 'key' });
        }

        // Active timer state (for crash recovery)
        if (!db.objectStoreNames.contains('timerState')) {
          db.createObjectStore('timerState', { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

// ============================================
// Sessions
// ============================================

export async function saveSession(session) {
  const db = await getDB();
  return db.put('sessions', session);
}

export async function getSession(id) {
  const db = await getDB();
  return db.get('sessions', id);
}

export async function getAllSessions() {
  const db = await getDB();
  return db.getAll('sessions');
}

export async function getSessionsByDate(date) {
  const db = await getDB();
  return db.getAllFromIndex('sessions', 'by-date', date);
}

export async function getSessionsInRange(startDate, endDate) {
  const db = await getDB();
  const range = IDBKeyRange.bound(startDate, endDate);
  return db.getAllFromIndex('sessions', 'by-date', range);
}

export async function getActiveSession() {
  const db = await getDB();
  const all = await db.getAllFromIndex('sessions', 'by-status', 'active');
  return all[0] || null;
}

// ============================================
// Timer State (crash recovery)
// ============================================

export async function saveTimerState(state) {
  const db = await getDB();
  return db.put('timerState', { key: 'current', ...state });
}

export async function getTimerState() {
  const db = await getDB();
  return db.get('timerState', 'current');
}

export async function clearTimerState() {
  const db = await getDB();
  return db.delete('timerState', 'current');
}

// ============================================
// User State
// ============================================

export async function getUserState(key) {
  const db = await getDB();
  const record = await db.get('userState', key);
  return record?.value;
}

export async function setUserState(key, value) {
  const db = await getDB();
  return db.put('userState', { key, value });
}

// Convenience wrappers for common user state keys

export async function getStreak() {
  return (await getUserState('streak')) || { count: 0, lastDate: null };
}

export async function setStreak(streak) {
  return setUserState('streak', streak);
}

export async function getXP() {
  return (await getUserState('xp')) || { total: 0, level: 1 };
}

export async function setXP(xp) {
  return setUserState('xp', xp);
}

export async function getOnboardingState() {
  return (await getUserState('onboarding')) || { completedFirstSession: false, sessionsCompleted: 0 };
}

export async function setOnboardingState(state) {
  return setUserState('onboarding', state);
}

export async function getSettings() {
  return (await getUserState('settings')) || {
    soundEnabled: false,
    notificationsEnabled: false,
    gracePeriodSeconds: 30,
  };
}

export async function setSettings(settings) {
  return setUserState('settings', settings);
}

// ============================================
// Anonymous User ID
// ============================================

export async function getUserId() {
  let id = await getUserState('userId');
  if (!id) {
    id = crypto.randomUUID();
    await setUserState('userId', id);
  }
  return id;
}

export async function setUserId(id) {
  return setUserState('userId', id);
}

// ============================================
// Clear All Data
// ============================================

export async function clearAllData() {
  const db = await getDB();
  const tx = db.transaction(['sessions', 'userState', 'timerState'], 'readwrite');
  await Promise.all([
    tx.objectStore('sessions').clear(),
    tx.objectStore('userState').clear(),
    tx.objectStore('timerState').clear(),
    tx.done,
  ]);
}

// ============================================
// Subscription State
// ============================================

const DEFAULT_SUBSCRIPTION = {
  tier: 'free',
  status: 'none',
  plan: null,
  trialStartDate: null,
  trialEndDate: null,
  currentPeriodEnd: null,
  stripeCustomerId: null,
  lastVerified: null,
};

export async function getSubscription() {
  return (await getUserState('subscription')) || { ...DEFAULT_SUBSCRIPTION };
}

export async function setSubscription(subscription) {
  return setUserState('subscription', { ...DEFAULT_SUBSCRIPTION, ...subscription });
}
